import 'easymde/dist/easymde.min.css';

import { Modal, ModalProps } from '@design/components/modal/Modal';
import { config } from '@terminal/config/app.config';
import clsx from 'clsx';
import EasyMDE from 'easymde';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './MarkdownEditor.module.scss';

export const EXPAND_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M13.375 1C12.7527 1 12.25 1.50273 12.25 2.125C12.25 2.74727 12.7527 3.25 13.375 3.25H15.1574L11.452 6.95547C11.0125 7.39492 11.0125 8.10859 11.452 8.54805C11.8914 8.9875 12.6051 8.9875 13.0445 8.54805L16.75 4.84258V6.625C16.75 7.24727 17.2527 7.75 17.875 7.75C18.4973 7.75 19 7.24727 19 6.625V2.125C19 1.50273 18.4973 1 17.875 1H13.375ZM8.54453 11.4555C8.10508 11.016 7.39141 11.016 6.95195 11.4555L3.25 15.1574V13.375C3.25 12.7527 2.74727 12.25 2.125 12.25C1.50273 12.25 1 12.7527 1 13.375V17.875C1 18.4973 1.50273 19 2.125 19H6.625C7.24727 19 7.75 18.4973 7.75 17.875C7.75 17.2527 7.24727 16.75 6.625 16.75H4.84258L8.54805 13.0445C8.9875 12.6051 8.9875 11.8914 8.54805 11.452L8.54453 11.4555Z" fill="var(--text-default)"/></svg>`;
const FULLSCREEN_MAX_HEIGHT = '600px';
const NORMAL_MIN_HEIGHT = '400px';

export interface MarkdownEditorProps {
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  minHeight?: string;
}

export const MarkdownEditor = ({
  placeholder = '',
  value,
  onChange,
  className = '',
  minHeight = NORMAL_MIN_HEIGHT,
}: MarkdownEditorProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const easyMDERef = useRef<EasyMDE | null>(null);
  const onChangeRef = useRef(onChange);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { t } = useTranslation('chat');

  // Keep the ref current without making it a dep of initializeEditor.
  // This prevents the editor from being destroyed/recreated on every keystroke
  // when the caller passes an inline arrow function as onChange.
  onChangeRef.current = onChange;

  const handleFullscreenToggle = () => {
    setIsFullscreen(!isFullscreen);
  };

  const isTeamsApp = config.app.isTeamsApp;

  const initializeEditor = useCallback(() => {
    if (textareaRef.current && !easyMDERef.current) {
      easyMDERef.current = new EasyMDE({
        element: textareaRef.current,
        initialValue: value,
        placeholder,
        spellChecker: false,
        autoDownloadFontAwesome: false,
        minHeight: isFullscreen ? FULLSCREEN_MAX_HEIGHT : minHeight,
        maxHeight: isFullscreen ? FULLSCREEN_MAX_HEIGHT : minHeight,
        status: false,
        lineWrapping: true,
        toolbar: !isFullscreen
          ? [
              {
                name: 'fullscreen',
                action: handleFullscreenToggle,
                className: 'expand-arrows',
                title: t('settings.fullscreen'),
                icon: EXPAND_ICON,
              },
            ]
          : false,
      });

      easyMDERef.current.codemirror.on('change', () => {
        if (easyMDERef.current) {
          onChangeRef.current(easyMDERef.current.value());
        }
      });
    }
  }, [isFullscreen, placeholder]);

  useEffect(() => {
    initializeEditor();

    return () => {
      if (easyMDERef.current) {
        easyMDERef.current.toTextArea();
        easyMDERef.current = null;
      }
    };
  }, [initializeEditor]);

  useEffect(() => {
    if (easyMDERef.current && value !== easyMDERef.current.value()) {
      easyMDERef.current.value(value);
    }
  }, [value]);

  const defaultCloseBtnProps: ModalProps['secondaryBtnProps'] = {
    onClick: handleFullscreenToggle,
    label: t('settings.close'),
    variant: 'tertiary',
  };

  const editorContent = (
    <div
      className={clsx(styles.editorWrapper, {
        [styles.isFullscreen]: isFullscreen,
        [styles.teamsApp]: isTeamsApp,
      })}
    >
      <textarea
        ref={textareaRef}
        className={className}
        data-testid="markdown-editor"
      />
    </div>
  );

  return (
    <>
      {isFullscreen ? (
        <Modal
          isOpen={isFullscreen}
          onClose={handleFullscreenToggle}
          secondaryBtnProps={defaultCloseBtnProps}
        >
          {editorContent}
        </Modal>
      ) : (
        editorContent
      )}
    </>
  );
};
